import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Grid, TextField, Button } from "@mui/material";
import { NotificationManager } from "react-notifications";
import { GetOrderResultById, SaveOrderResult, UpdateOrderResult } from "../../services/OrderService";
import CommonLoader from "../common/CommonLoader";
import { isNoDate } from "../../../services/dateService";

const EditOrderResult = () => {
    const { orderNumber, recordId } = useParams();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        orderNumber: "",
        sampleNumber: "",
        testCode: "",
        parameter: "",
        testCategory: "",
        clientJobNumber: "",
        globalJobNumber: "",
        testType: "",
        analyticalValue: "",
        methodCode: "",
        numericVal1: "",
        numericVal2: "",
        numericVal3: "",
        numericVal4: "",
        numericVal5: "",
        numericVal6: "",
        numericVal7: "",
        numericVal8: "",
        numericVal9: "",
        numericVal10: "",
        numericVal11: "",
        numericVal12: "",
        numericVal13: "",
        numericVal14: "",
        numericVal15: "",
        numericVal16: "",
        numericVal17: "",
        numericVal18: "",
        numericVal19: "",
        numericVal20: "",
        textVal1: "",
        textVal2: "",
        textVal3: "",
        units: "",
        detectionLimit: "",
        minControlLimit: "",
        maxControlLimit: "",
        sigfigs: 0,
        testingStatus: "",
        analyst: "",
        expirationDate: "",
        dateOfAnalysis: "",
        reportRemarks: "",
        comments: "",
        sortIndex: 0,
        accountNumber: "",
        machineId: "",
        qcPosted: false,
        batchId: 0,
        prepDate: "",
        formulaId: "",
        footer1: "",
        footer2: "",
        footer3: "",
        testimage: "",
        wordfile: "",
        excelfile: "",
        approvedBy: "",
        dilution: 0,
        departmentCode: "",
        detectionSymbol: "",
        processId: "",
        reportingLimit: "",
        dateEntered: "",
        recordid: "",
        controlLimitType: 0,
        createdByUser: "",
        createdByTime: "",
        lastModifiedUser: "",
        lastModifiedTime: "",
        standardDeviation: 0,
    });

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const isEditing = Boolean(recordId);

    useEffect(() => {
        if (isEditing) {
            fetchResult();
        } else {
            setFormData(prev => ({ ...prev, orderNumber }));
        }
    }, [orderNumber, recordId]);

    const fetchResult = async () => {
        try {
            setLoading(true);
            const response = await GetOrderResultById(orderNumber, recordId);
            if (response.statusCode === 200) {
                const result = response.results;
                setFormData({
                    ...formData,
                    ...result,
                    // Dates are used exactly as the API sends them, just cut to what the
                    // input needs. Other date fields pass through untouched.
                    expirationDate: result.expirationDate ? result.expirationDate.slice(0, 10) : "",
                    dateOfAnalysis: isNoDate(result.dateOfAnalysis) ? "" : result.dateOfAnalysis.slice(0, 16),
                });
            } else {
                NotificationManager.error("Error fetching order result");
            }
        } catch (err) {
            NotificationManager.error("Error fetching order result");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === "checkbox" ? checked : value,
        });
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.orderNumber) errors.orderNumber = "Order Number is required";
        if (!formData.sampleNumber) errors.sampleNumber = "Sample Number is required";
        if (!formData.dateOfAnalysis) errors.dateOfAnalysis = "Date of Analysis is required";
        setErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSaveClick = async () => {        
        if (!validateForm()) return;

        const payload = {
            ...formData,
            // Dates go back exactly as entered; empty ones as null.
            dateOfAnalysis: formData.dateOfAnalysis || null,
            expirationDate: formData.expirationDate || null,
            prepDate: formData.prepDate || null,
            dateEntered: formData.dateEntered || null,
            createdByTime: formData.createdByTime || null,
            lastModifiedTime: formData.lastModifiedTime || null,
        };

        try {
            setLoading(true);
            let response;
            if (isEditing) {
                response = await UpdateOrderResult(payload);
            } else {
                response = await SaveOrderResult(payload);
            }

            if (response.status === 200) {
                NotificationManager.success(isEditing ? "Order Result updated successfully" : "Order Result created successfully");
                navigate(`/vlp/edit/lab-orders/${orderNumber}`);
            } else {
                NotificationManager.error(isEditing ? "Error updating Order Result" : "Error creating Order Result");
            }
        } catch (error) {
            NotificationManager.error(isEditing ? "Error updating Order Result" : "Error creating Order Result");
        } finally {
            setLoading(false);
        }
    };
    const handleCancel = () => {
        navigate(`/vlp/edit/lab-orders/${orderNumber}`);
    };

    return (
        <div style={{ padding: "16px" }}>
            {loading && <CommonLoader />}
            <h2>{isEditing ? "Edit Order Result" : "Add New Order Result"}</h2>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth
                        name="orderNumber"
                        label="Order Number"
                        value={formData.orderNumber}
                        onChange={handleInputChange}
                        margin="dense"
                        variant="outlined"
                        helperText={errors.orderNumber}
                        error={!!errors.orderNumber}
                        size="small"
                        disabled
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth
                        name="sampleNumber"
                        label="Sample Number"
                        value={formData.sampleNumber}
                        onChange={handleInputChange}
                        margin="dense"
                        variant="outlined"
                        helperText={errors.sampleNumber}
                        error={!!errors.sampleNumber}
                        size="small"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth
                        name="testCode"
                        label="Test Code"
                        value={formData.testCode}
                        onChange={handleInputChange}
                        margin="dense"
                        variant="outlined"
                        size="small"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth
                        name="parameter"
                        label="Parameter"
                        value={formData.parameter}
                        onChange={handleInputChange}
                        margin="dense"
                        variant="outlined"
                        size="small"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth
                        name="testCategory"
                        label="Test Category"
                        value={formData.testCategory}
                        onChange={handleInputChange}
                        margin="dense"
                        variant="outlined"
                        size="small"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth
                        name="clientJobNumber"
                        label="Client Job Number"
                        value={formData.clientJobNumber}
                        onChange={handleInputChange}
                        margin="dense"
                        variant="outlined"
                        size="small"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth
                        name="globalJobNumber"
                        label="Global Job Number"
                        value={formData.globalJobNumber}
                        onChange={handleInputChange}
                        margin="dense"
                        variant="outlined"
                        size="small"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth
                        name="testType"
                        label="Test Type"
                        value={formData.testType}
                        onChange={handleInputChange}
                        margin="dense"
                        variant="outlined"
                        size="small"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth
                        name="analyticalValue"
                        label="Analytical Value"
                        value={formData.analyticalValue}
                        onChange={handleInputChange}
                        margin="dense"
                        variant="outlined"
                        size="small"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth
                        name="methodCode"
                        label="Method Code"
                        value={formData.methodCode}
                        onChange={handleInputChange}
                        margin="dense"
                        variant="outlined"
                        size="small"
                    />
                </Grid>
                {/* Numeric Values */}

                {/* Text Values */}

                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth
                        name="units"
                        label="Units"
                        value={formData.units}
                        onChange={handleInputChange}
                        margin="dense"
                        variant="outlined"
                        size="small"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth
                        name="detectionLimit"
                        label="Detection Limit"
                        value={formData.detectionLimit}
                        onChange={handleInputChange}
                        margin="dense"
                        variant="outlined"
                        size="small"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth
                        name="minControlLimit"
                        label="Min Control Limit"
                        value={formData.minControlLimit}
                        onChange={handleInputChange}
                        margin="dense"
                        variant="outlined"
                        size="small"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth
                        name="maxControlLimit"
                        label="Max Control Limit"
                        value={formData.maxControlLimit}
                        onChange={handleInputChange}
                        margin="dense"
                        variant="outlined"
                        size="small"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth
                        name="sigfigs"
                        label="Significant Figures"
                        value={formData.sigfigs}
                        onChange={handleInputChange}
                        margin="dense"
                        variant="outlined"
                        size="small"
                        type="number"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth
                        name="testingStatus"
                        label="Testing Status"
                        value={formData.testingStatus}
                        onChange={handleInputChange}
                        margin="dense"
                        variant="outlined"
                        size="small"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth
                        name="analyst"
                        label="Analyst"
                        value={formData.analyst}
                        onChange={handleInputChange}
                        margin="dense"
                        variant="outlined"
                        size="small"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth
                        name="expirationDate"
                        label="Expiration Date"
                        type="date"
                        value={formData.expirationDate}
                        onChange={handleInputChange}
                        margin="dense"
                        variant="outlined"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth
                        name="dateOfAnalysis"
                        label="Date/Time of Analysis"
                        type="datetime-local"
                        value={formData.dateOfAnalysis}
                        onChange={handleInputChange}
                        margin="dense"
                        variant="outlined"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth
                        name="reportRemarks"
                        label="Report Remarks"
                        value={formData.reportRemarks}
                        onChange={handleInputChange}
                        margin="dense"
                        variant="outlined"
                        size="small"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth
                        name="comments"
                        label="Comments"
                        value={formData.comments}
                        onChange={handleInputChange}
                        margin="dense"
                        variant="outlined"
                        size="small"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth
                        name="sortIndex"
                        label="Sort Index"
                        value={formData.sortIndex}
                        onChange={handleInputChange}
                        margin="dense"
                        variant="outlined"
                        size="small"
                        type="number"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth
                        name="accountNumber"
                        label="Account Number"
                        value={formData.accountNumber}
                        onChange={handleInputChange}
                        margin="dense"
                        variant="outlined"
                        size="small"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth
                        name="machineId"
                        label="Machine ID"
                        value={formData.machineId}
                        onChange={handleInputChange}
                        margin="dense"
                        variant="outlined"
                        size="small"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth
                        name="machineName"
                        label="Machine Name"
                        value={formData.machineName}
                        onChange={handleInputChange}
                        margin="dense"
                        variant="outlined"
                        size="small"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth
                        name="machineSerialNumber"
                        label="Machine Serial Number"
                        value={formData.machineSerialNumber}
                        onChange={handleInputChange}
                        margin="dense"
                        variant="outlined"
                        size="small"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth
                        name="operatorName"
                        label="Operator Name"
                        value={formData.operatorName}
                        onChange={handleInputChange}
                        margin="dense"
                        variant="outlined"
                        size="small"
                    />
                </Grid>
                {Array.from({ length: 3 }, (_, index) => (
                    <Grid item xs={12} sm={6} md={3} key={`textVal${index + 1}`}>
                        <TextField
                            fullWidth
                            name={`textVal${index + 1}`}
                            label={`Text Value ${index + 1}`}
                            value={formData[`textVal${index + 1}`]}
                            onChange={handleInputChange}
                            margin="dense"
                            variant="outlined"
                            size="small"
                            multiline
                        />
                    </Grid>
                ))}
                {Array.from({ length: 20 }, (_, index) => (
                    <Grid item xs={12} sm={6} md={3} key={`numericVal${index + 1}`}>
                        <TextField
                            fullWidth
                            name={`numericVal${index + 1}`}
                            label={`Numeric Value ${index + 1}`}
                            value={formData[`numericVal${index + 1}`]}
                            onChange={handleInputChange}
                            margin="dense"
                            variant="outlined"
                            size="small"
                        />
                    </Grid>
                ))}

            </Grid>
            <div style={{ marginTop: 16 }}>
                <Button variant="contained" color="primary" onClick={(e) => {
                    console.log('Button clicked!', e);
                    handleSaveClick();
                }}>
                    Save
                </Button>
                <Button style={{ marginLeft: 8 }} onClick={handleCancel} >
                    Cancel
                </Button>
            </div>
        </div>
    );
};

export default EditOrderResult;
